import React, { useState } from 'react';
import styles from './EventPreview.module.css';
import pageStyles from '../pages/CreateEventPage.module.css';
import EventCard from './EventCard';
import { Event, Organization } from '../types/event';
import { IconEye, IconEyeOff, IconCards, IconLayoutList } from '@tabler/icons-react';

interface EventPreviewProps {
  formData: any;
  visible: boolean;
  onToggleVisibility: () => void;
}

/**
 * Component that provides a live preview of how an event will appear
 * in the EventCard component based on form data
 */
const EventPreview: React.FC<EventPreviewProps> = ({ formData, visible, onToggleVisibility }) => {
  const [showPanel, setShowPanel] = useState(false);

  if (!visible) {
    return (
      <div className={pageStyles.previewToggleContainer}>
        <button 
          type="button" 
          className={pageStyles.previewToggleButton}
          onClick={onToggleVisibility}
        >
          <IconEye size={18} />
          <span>Afficher l'aperçu</span>
        </button>
      </div>
    );
  }

  // Convert form data to an event object for the EventCard component
  const previewEvent: Event = {
    id: 0,
    title: formData.title || 'Titre de l\'événement',
    description: formData.description || 'Description de l\'événement',
    dateTime: formData.dateTime || new Date().toISOString(),
    location: formData.location || 'Lieu de l\'événement',
    eventCategory: {
      id: formData.category || 'other',
      code: formData.category || 'other',
      name: formData.category || 'Autre',
      color: '#6366f1'
    },
    subcategory: formData.subcategory || '',
    tags: formData.tags || '',
    maxParticipants: formData.maxParticipants || null,
    imageUrl: formData.imageUrl || '',
    organizationId: formData.organizationId || 1,
    // Full organization object to comply with the type
    organization: {
      id: formData.organizationId || 1,
      name: 'Prévisualisation',
      description: '',
      email: '',
      address: '',
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    } as Organization,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    _count: {
      interests: 0,
      savedBy: 0,
      views: 0
    },
    // Coordinates if available
    coordinates: formData.coordinates,
    isInterested: false,
    isSaved: false
  };

  return (
    <div className={pageStyles.previewSection}>
      <div className={pageStyles.previewHeader}>
        <h2>Prévisualisation</h2>
        <div className={pageStyles.previewToggleButtons}>
          <button 
            type="button" 
            className={`${pageStyles.previewToggleButton} ${!showPanel ? pageStyles.active : ''}`}
            onClick={() => setShowPanel(false)}
            title="Afficher la carte d'événement"
          >
            <IconCards size={18} />
            <span>Carte</span>
          </button>
          <button 
            type="button" 
            className={`${pageStyles.previewToggleButton} ${showPanel ? pageStyles.active : ''}`}
            onClick={() => setShowPanel(true)}
            title="Afficher le panneau d'informations"
          >
            <IconLayoutList size={18} />
            <span>Panneau</span>
          </button>
          <button 
            type="button" 
            className={pageStyles.previewToggleButton}
            onClick={onToggleVisibility}
            title="Masquer l'aperçu"
          >
            <IconEyeOff size={18} />
            <span>Masquer</span>
          </button>
        </div>
      </div>
      
      <div className={pageStyles.previewContent}>
        {!showPanel ? (
          <div className={pageStyles.previewCardContainer}>
            <EventCard
              event={previewEvent}
              onInterestClick={() => {}}
              onSaveClick={() => {}}
              showStatus={false}
            />
          </div>
        ) : (
          <div className={pageStyles.previewPanelContainer}>
            <div className={styles.eventPanel}>
              <div className={styles.panelHeader}>
                <h2>{previewEvent.title || 'Titre de l\'événement'}</h2>
                <div className={styles.panelCategory} style={{ backgroundColor: '#6366f1' }}>
                  {previewEvent.eventCategory.name}
                </div>
              </div>
              <div className={styles.panelDetails}>
                <div className={styles.panelDetail}>
                  <strong>Date et heure:</strong>
                  <span>{formData.dateTime ? new Date(formData.dateTime).toLocaleString('fr-FR') : 'Non spécifié'}</span>
                </div>
                <div className={styles.panelDetail}>
                  <strong>Lieu:</strong>
                  <span>{formData.location || 'Non spécifié'}</span>
                </div>
                <div className={styles.panelDetail}>
                  <strong>Organisation:</strong>
                  <span>Prévisualisation</span>
                </div>
                <div className={styles.panelDetail}>
                  <strong>Participants max:</strong>
                  <span>{formData.maxParticipants || 'Illimité'}</span>
                </div>
              </div>
              <div className={styles.panelDescription}>
                <h3>Description</h3>
                <p>{formData.description || 'Description de l\'événement...'}</p>
              </div>
              {formData.tags && (
                <div className={styles.panelTags}>
                  {formData.tags.split(',').map((tag: string, index: number) => (
                    <span key={index} className={styles.tag}>{tag.trim()}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      <div className={styles.previewNote}>
        <p>Cette prévisualisation montre comment votre événement apparaîtra aux utilisateurs. Les dimensions sont identiques à l'application finale.</p>
      </div>
    </div>
  );
};

export default EventPreview;
